import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, Edit, Upload, Download, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { DataTable, Column } from '@/components/admin/DataTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';

interface Quote {
  id: number;
  text: string;
  author: string;
  authorSlug: string;
  category: string;
  language: string;
  isPublished: boolean;
  isFeatured: boolean;
  views: number;
  shares: number;
  createdAt: string;
}

export function QuoteManagerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; id?: number }>({ open: false });
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);

  const { data: quotesData, isLoading } = useQuery({
    queryKey: ['admin-quotes', page, search],
    queryFn: async () => {
      const params: Record<string, string> = { page: String(page), limit: '20' };
      if (search) params.search = search;
      const res = await api.quotes.getAll(params);
      return res as { success: boolean; data: Quote[]; totalPages: number; total: number };
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.admin.deleteQuote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quotes'] });
      setDeleteModal({ open: false });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: number[]) => api.admin.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quotes'] });
      setSelectedIds([]);
      setBulkDeleteModal(false);
    },
  });

  const importMutation = useMutation({
    mutationFn: (quotes: any[]) => api.admin.importQuotes(quotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quotes'] });
    },
  });

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      let quotes: any[];

      if (file.name.endsWith('.json')) {
        quotes = JSON.parse(text);
      } else if (file.name.endsWith('.csv')) {
        const lines = text.split('\n');
        const headers = lines[0].split(',').map((h) => h.trim());
        quotes = lines.slice(1).filter(Boolean).map((line) => {
          const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
          const obj: Record<string, string> = {};
          headers.forEach((h, i) => { obj[h] = values[i] || ''; });
          return obj;
        });
      } else {
        alert('Unsupported file format. Use JSON or CSV.');
        return;
      }

      await importMutation.mutateAsync(quotes);
    } catch {
      alert('Failed to parse file');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.admin.exportQuotes();
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'quotes-export.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export quotes');
    }
  };

  const columns: Column<Quote>[] = [
    {
      key: 'text',
      header: 'Quote',
      sortable: true,
      render: (quote) => (
        <span className="line-clamp-2 text-surface-800">{quote.text}</span>
      ),
    },
    {
      key: 'author',
      header: 'Author',
      sortable: true,
      render: (quote) => (
        <span className="text-surface-600">{quote.author}</span>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (quote) => (
        <span className="inline-block px-2 py-1 text-xs rounded-full bg-surface-100 text-surface-600">
          {quote.category}
        </span>
      ),
    },
    {
      key: 'isPublished',
      header: 'Status',
      render: (quote) => (
        <span
          className={`inline-block px-2 py-1 text-xs rounded-full ${
            quote.isPublished
              ? 'bg-green-100 text-green-700'
              : 'bg-yellow-100 text-yellow-700'
          }`}
        >
          {quote.isPublished ? 'Published' : 'Draft'}
        </span>
      ),
    },
    {
      key: 'views',
      header: 'Views',
      sortable: true,
      render: (quote) => (
        <span className="text-surface-600">{quote.views?.toLocaleString()}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (quote) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(`/admin/quotes/edit/${quote.id}`)}
            className="p-2 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-primary-600 transition-colors"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => setDeleteModal({ open: true, id: quote.id })}
            className="p-2 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-red-500 transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-surface-900">Quotes</h1>
          <p className="text-surface-500 mt-1">
            Manage all quotes in your platform
          </p>
        </div>
        <Button onClick={() => navigate('/admin/quotes/new')}>
          <Plus size={18} />
          New Quote
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-400" />
          <Input
            placeholder="Search quotes..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-11"
          />
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,.csv"
            onChange={handleImport}
            className="hidden"
          />
          <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={importMutation.isPending}>
            <Upload size={18} />
            Import
          </Button>
          <Button variant="secondary" onClick={handleExport}>
            <Download size={18} />
            Export
          </Button>
          {selectedIds.length > 0 && (
            <Button variant="danger" onClick={() => setBulkDeleteModal(true)}>
              <Trash2 size={18} />
              Delete ({selectedIds.length})
            </Button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={quotesData?.data ?? []}
        loading={isLoading}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        page={page}
        totalPages={quotesData?.totalPages ?? 1}
        onPageChange={setPage}
        emptyMessage="No quotes found"
      />

      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false })}
        title="Delete Quote"
      >
        <p className="text-surface-600 mb-6">
          Are you sure you want to delete this quote? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setDeleteModal({ open: false })}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteModal.id && deleteMutation.mutate(deleteModal.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        title="Bulk Delete"
      >
        <div className="flex items-start gap-3 mb-6 p-4 rounded-xl bg-red-50 text-red-600">
          <AlertCircle size={20} className="mt-0.5 shrink-0" />
          <p className="text-sm">
            Are you sure you want to delete {selectedIds.length} quotes? This action cannot be undone.
          </p>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setBulkDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => bulkDeleteMutation.mutate(selectedIds)}
            disabled={bulkDeleteMutation.isPending}
          >
            {bulkDeleteMutation.isPending ? 'Deleting...' : `Delete ${selectedIds.length} Quotes`}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
